import React, {useCallback} from 'react'
import {
  AuiIf,
  ComposerPrimitive,
  useComposerRuntime,
} from '@assistant-ui/react'

import type {FileTag} from '@/shared/utils/crud-helper/models'
import type {ChatModelOption} from '../ai-chat/BedrockSocketProvider'
import {OmicsContextRing} from '../ai-chat/OmicsContextRing'
import {EMPTY_CONTEXT_USAGE, formatContextWindowTitle, type ContextUsage} from '../ai-chat/omicsContextUsage'
import type {PendingRun} from '../ai-chat/pendingRuns'
import {useFileImportActions} from '../FileImportActionsContext'
import {ImportDataDropdown} from '../ImportDataDropdown'
import {useLayoutPanelsOptional} from '@/shared/context/ChatContext'
import {OmicsComposerModelPicker} from './OmicsComposerModelPicker'
import {ComposerCloudUnitsBanner} from './ComposerCloudUnitsBanner'
import {ComposerSubscriptionBanner} from './ComposerSubscriptionBanner'
import {FileTagChip} from './FileTagChip'

export interface OmicsComposerProps {
  fileTags: FileTag[]
  onRemoveTag: (id: string) => void
  handleDragOver: (e: React.DragEvent) => void
  handleDragEnter: (e: React.DragEvent) => void
  handleDragLeave: (e: React.DragEvent) => void
  handleDrop: (e: React.DragEvent) => void
  isRunning: boolean
  threadContextUsage: ContextUsage | null
  lastUserPrompt: string | null
  pendingRuns: PendingRun[]
  onDismissPendingRun: (runId: string) => void
  availableModels: ChatModelOption[]
  selectedModelId: string | null
  onSelectModel: (id: string) => void
  /** True when a DictationAdapter is on the runtime (browser Web Speech). */
  dictationEnabled: boolean
  /**
   * New turns are blocked (no cloud units left, or an expired subscription): the input stays
   * editable but cannot send. Stop still works.
   */
  sendLocked?: boolean
  /** Tooltip on the disabled send button, naming why sending is locked. */
  sendLockedTitle?: string
}

/**
 * assistant-ui composer chrome: one bordered card with attachments, input, and
 * an in-card toolbar (model, import data, dictation, context ring, send/stop).
 *
 * Slash commands / @mentions for launching skills can wrap this in
 * `ComposerPrimitive.Unstable_TriggerPopoverRoot` later without changing the card layout.
 */
export const OmicsComposer: React.FC<OmicsComposerProps> = ({
  fileTags,
  onRemoveTag,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  isRunning,
  threadContextUsage,
  lastUserPrompt,
  pendingRuns,
  onDismissPendingRun,
  availableModels,
  selectedModelId,
  onSelectModel,
  dictationEnabled,
  sendLocked = false,
  sendLockedTitle = 'No cloud units left',
}) => {
  const composer = useComposerRuntime()

  const handleComposerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key !== 'ArrowUp' || !lastUserPrompt || e.currentTarget.value.trim() !== '') {
        return
      }
      e.preventDefault()
      composer.setText(lastUserPrompt)
    },
    [composer, lastUserPrompt],
  )

  const contextTitle = formatContextWindowTitle((threadContextUsage ?? EMPTY_CONTEXT_USAGE).percent)
  const sendOrStop = isRunning ? (
    <ComposerPrimitive.Cancel
      className='aui-composer-stop'
      title='Stop and cancel'
      type='button'
    >
      <i className='fas fa-stop' />
    </ComposerPrimitive.Cancel>
  ) : sendLocked ? (
    <button
      type='button'
      className='aui-composer-send'
      title={sendLockedTitle}
      disabled
      data-testid='omics-ai-chat-send-locked'
    >
      <i className='fas fa-arrow-up' />
    </button>
  ) : (
    <ComposerPrimitive.Send className='aui-composer-send' title={contextTitle}>
      <i className='fas fa-arrow-up' />
    </ComposerPrimitive.Send>
  )

  return (
    <ComposerPrimitive.Root
      className='aui-composer-root'
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className='aui-composer-shell' data-testid='omics-ai-chat-composer-shell'>
        <ComposerSubscriptionBanner />
        <ComposerCloudUnitsBanner />
        {fileTags.length > 0 && (
          <div className='file-tags-container'>
            {fileTags.map((tag) => (
              <FileTagChip key={tag.id} tag={tag} onRemove={onRemoveTag} />
            ))}
          </div>
        )}
        {pendingRuns.length > 0 && (
          <div className='aui-pending-runs'>
            {pendingRuns.map((r) => {
              const finished = Boolean(r.resolution)
              return (
                <span
                  key={r.runId}
                  className={`aui-pending-run${finished ? ' aui-pending-run--finished' : ''}`}
                  title={`run_id: ${r.runId}`}
                >
                  <i className={finished ? 'fas fa-circle-check' : 'fas fa-hourglass-half'} />
                  <span>
                    {finished
                      ? `${r.tool || 'Job'} finished — picking up the results`
                      : `Running ${r.tool || 'job'} — I'll show the results here when it finishes`}
                  </span>
                  <button
                    type='button'
                    className='aui-pending-run__remove'
                    onClick={() => onDismissPendingRun(r.runId)}
                    title='Dismiss'
                  >
                    <i className='fas fa-xmark' />
                  </button>
                </span>
              )
            })}
          </div>
        )}
        <ComposerPrimitive.Input
          placeholder='Search, analyse, study'
          className='aui-composer-input'
          submitOnEnter={!sendLocked}
          onKeyDown={handleComposerKeyDown}
        />
        <div className='aui-composer-actions'>
          <div className='aui-composer-actions__start'>
            <OmicsComposerModelPicker
              models={availableModels}
              selectedModelId={selectedModelId}
              onSelect={onSelectModel}
            />
          </div>
          <div className='aui-composer-actions__end'>
            <ComposerImportDataButton />
            {dictationEnabled ? <OmicsComposerDictation /> : null}
            <OmicsContextRing usage={threadContextUsage ?? EMPTY_CONTEXT_USAGE}>
              {sendOrStop}
            </OmicsContextRing>
          </div>
        </div>
      </div>
    </ComposerPrimitive.Root>
  )
}

const ComposerImportDataButton: React.FC = () => {
  const actions = useFileImportActions()
  const layout = useLayoutPanelsOptional()

  if (!actions) {
    return null
  }

  const run = (fn: () => void) => {
    // The ImportWizard and the destination folder live in the Files panel; open it so a
    // composer-triggered import is not trapped in `display: none`.
    layout?.openFilesPanel()
    fn()
  }

  return (
    <ImportDataDropdown
      appearance='composer'
      testId='omics-ai-chat-import-btn'
      onUploadFile={() => run(actions.onUploadFile)}
      onUploadFolder={() => run(actions.onUploadFolder)}
      onImportS3={(sourceType) => run(() => actions.onImportS3(sourceType))}
    />
  )
}

const OmicsComposerDictation: React.FC = () => {
  return (
    <>
      <AuiIf condition={(s) => s.composer.dictation == null}>
        <ComposerPrimitive.Dictate
          className='aui-composer-dictate'
          title='Voice input'
          type='button'
          aria-label='Start voice input'
          data-testid='omics-ai-chat-dictate-btn'
        >
          <i className='fas fa-microphone' aria-hidden />
        </ComposerPrimitive.Dictate>
      </AuiIf>
      <AuiIf condition={(s) => s.composer.dictation != null}>
        <ComposerPrimitive.StopDictation
          className='aui-composer-stop-dictation'
          title='Stop dictation'
          type='button'
          aria-label='Stop voice input'
          data-testid='omics-ai-chat-stop-dictation-btn'
        >
          <i className='fas fa-stop' aria-hidden />
        </ComposerPrimitive.StopDictation>
      </AuiIf>
    </>
  )
}
